import React, { useContext } from 'react'
import ReactSelect, { components as reactSelectComponents } from 'react-select'
import ReactSelectAsync from 'react-select/async'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import Icon from '../Icon'

const Control = ({ children, compact = false, className, ...props }) => {
  const { icon = null, inputValue, floatingLabel, selectProps } = props.selectProps

  return (
    <reactSelectComponents.Control
      {...props}
      className={classNames(className, [
        'border',
        'focus:border-primary',
        'transition',
        'transition-duration-200',
        'w-full',
        'relative',
      ], {
        xs:   'p-1',
        sm:   'p-1 sm:p-2',
        base: 'p-1 sm:p-2 md:p-4',
        lg:   'p-1 sm:p-2 md:p-4 lg:p-6',
      }[selectProps?.size] ?? selectProps?.size, {
        'border-primary': props.isFocused,
        'border-gray-200': !props.isFocused,
      })}
    >
      { icon &&
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center p-4">
          <FontAwesomeIcon icon={`fa-solid fa-${icon}`} className="mr-1 text-sm" />
        </div>
      }

      { floatingLabel &&
        <div className={classNames('absolute -top-2 font-medium text-xs transition-all transition-duration-200 bg-white', {
          'opacity-100': !!inputValue.length || props.hasValue,
          'opacity-0': props.isFocused ? !inputValue.length : !props.hasValue,
          'text-primary': props.isFocused,
          'text-gray-200': !props.isFocused,
        }, {
          xs:   'left-1.5 px-0',
          sm:   'left-1.5 px-0 sm:left-2 sm:px-0.4',
          base: 'left-1.5 px-0 sm:left-2 sm:px-0.4 md:left-3.5 md:px-1',
          lg:   'left-1.5 px-0 sm:left-2 sm:px-0.4 md:left-3.5 md:px-1 lg:left-5 lg:px-1.5',
        }[selectProps?.size] ?? '')}>{ floatingLabel }</div>
      }

      <div className={classNames({ 'pl-5': !!icon })}>
        { children }
      </div>
    </reactSelectComponents.Control>
  )
}

const DropdownIndicator = (props) => {
  return (
    <reactSelectComponents.DropdownIndicator {...props}>
      <div className="absolute inset-y-0 right-0 flex items-center px-2 text-inherit cursor-pointer">
        <FontAwesomeIcon icon="fa-solid fa-chevron-down" className="mr-1 text-sm" />
      </div>
    </reactSelectComponents.DropdownIndicator>
  )
}

export const selectComponents = {
  Control,
  DropdownIndicator,
}

const defaultStyles = {
  multiValueLabel: (base) => ({
    ...base,
    padding: '0 0.5em 0 0.5em',
    paddingLeft: '0.5em',
  }),
  menu: (styles) => ({
    ...styles,
    width: 'max-content',
    minWidth: '100%',
    borderRadius: 0,
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  container: (provided, state) => ({
    ...provided,
    width: '100%',
  }),
  control: (provided, state) => ({
    width: '100%',
    appearance: 'none',
    minHeight: '1px',
  }),
  input: (provided, state) => ({
    ...provided,
    margin: 0,
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: 0,
    paddingRight: '1.5em',
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
  input: (provided, state) => ({
    ...provided,
    padding: 0,
    margin: 0,
  }),
}

const SelectWrapper = ({
  element: Component,
  label,
  optional = false,
  required = false,
  description,
  labelClassName,
  descriptionClassName,
  optionalClassName,
  requiredClassName,
  children,
  errors = [],
  containerProps = {},
  ...props
}) => {
  return (
    <div className="space-y-1" {...containerProps}>
      { (label || description) &&
        <div>
          <div className="flex text-sm font-medium space-x-1">
            { label &&
              <div className="flex text-sm font-medium space-x-1">
                <label className={classNames(['text-gray-700'], labelClassName)}>
                  { label }
                </label>
              </div>
            }
            { optional &&
              <span className={classNames(['italic', 'font-normal', 'text-gray-400'], optionalClassName)}>
                Optional
              </span>
            }
            { required &&
              <span className={classNames(['font-normal', 'text-negative'], requiredClassName)}>
                *
              </span>
            }
          </div>
          { description &&
            <div>
              <label className={classNames(['text-gray-700', 'font-normal', 'text-xs'], descriptionClassName)}>
                { description }
              </label>
            </div>
          }
        </div>
      }
      { Component ? (
        <Component {...props} > 
          { children }
        </Component>
      ) : children }

      { !!errors.length &&
        <div className="text-xs flex items-center space-x-2">
          <Icon icon={['fas', 'warning']} className="text-negative" />

          <div>
            { errors.map((error) => (
              <div className="text-gray-600">{ error }</div>
            )) }
          </div>
        </div>
      }
    </div>
  )
}

const getValueProps = (props) => {
  if (props.value == null && props.noDataLabel) {
    return { value: '', label: props.noDataLabel }
  } else {
    return props.value;
  }
}

const SelectElement = ({
  size = 'base',
  selectProps = {},
  styles,
  components = {},
  ...props
}) => (
    <ReactSelect
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      selectProps={{
        size,
        ...selectProps,
      }}
      styles={{
        ...defaultStyles,
        ...styles
      }}
      components={{
        ...reactSelectComponents,
        ...selectComponents,
        ...components,
      }}
      {...props}
      value={getValueProps(props)}
    />
)

const SelectAsyncElement = ({
  size = 'base',
  selectProps = {},
  styles,
  components = {},
  ...props
}) => (
    <ReactSelectAsync
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      selectProps={{
        size,
        ...selectProps,
      }}
      styles={{
        ...defaultStyles,
        ...styles
      }}
      components={{
        ...reactSelectComponents,
        ...selectComponents,
        ...components,
      }}
      {...props}
      value={getValueProps(props)}
    />
)

export const Select = (props) => (
  <SelectWrapper {...props} element={SelectElement} />
)

export const SelectAsync = (props) => (
  <SelectWrapper {...props} element={SelectAsyncElement} />
)
